import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, useTheme, Dialog, DialogActions, DialogContent, DialogTitle, useMediaQuery } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { tokens } from '../../../theme';
import { deleteVendor, getVendors } from '../../../redux/slice/vendors.slice';
import Header from "../../../components/Header";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { Country, State, City } from 'country-state-city';

const VendorDataGrid = ({ data, columns }) => {
    return (
        <DataGrid
            rows={data}
            columns={columns}
            components={{ Toolbar: GridToolbar }}
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
            getRowId={(row) => row._id}
            density="compact"
            autoHeight
            sx={{
                "@media print": {
                    "& .MuiDataGrid-toolbarContainer": {
                        display: "none !important"
                    },
                    "& .MuiDataGrid-root": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-cell": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-footerContainer": {
                        color: "black !important",
                    },
                    "& .css-7ms3qr-MuiTablePagination-displayedRows": {
                        color: "black !important",
                    },
                },
            }}
        />
    );
};

const Vendor = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const colors = tokens(theme.palette.mode);
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [openView, setOpenView] = React.useState(false);
    const [viewData, setViewData] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteVendorData, setDeleteVendorData] = useState(null);
    const vendors = useSelector(state => state.vendors.vendors);
    const isMobile = useMediaQuery('(max-width:500px)');

    useEffect(() => {
        dispatch(getVendors());
    }, []);

    const handleDelete = (id) => {
        setDeleteVendorData(id);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (deleteVendorData) {
            dispatch(deleteVendor(deleteVendorData));
            setDeleteConfirmOpen(false);
            setDeleteVendorData(null);
        }
    }
    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setDeleteVendorData(null);
    }
    const handleEdit = (data) => {
        navigate('/admin/add-vendor', { state: data });
    };

    const handleAddVendor = () => {
        navigate('/admin/add-vendor')
    }
    const handleClickOpenView = () => {
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
    };
    const handleView = (data) => {
        handleClickOpenView();
        const countryName = Country.getCountryByCode(data.country)?.name || data.country;
        const stateName = State.getStateByCodeAndCountry(data.state, data.country)?.name || data.state;

        setViewData({
            ...data,
            country: countryName,
            state: stateName
        })
    }

    const columns = useMemo(() => [
        {
            field: 'Sr no',
            headerName: 'Sr No',
            width: 50,
            valueGetter: (params) => {
                const index = params.api.getAllRowIds().indexOf(params.row._id);
                return index + 1;
            }
        },
        { field: 'businessName', headerName: 'Business Name', width: 350 },
        { field: 'vendorType', headerName: 'Vendor Type', width: 150 },
        {
            field: 'contactPersonName', headerName: 'Contact Person Name', width: 200,
            filterable: true,
            valueGetter: (params) => {
                return params.row.contactPersonalDetails[0]?.contactPersonName;
            },
            renderCell: (params) => {
                return params.row.contactPersonalDetails[0]?.contactPersonName;
            }
        },
        {
            field: 'contactNumber', headerName: 'Contact Number', width: 180,
            filterable: true,
            valueGetter: (params) => {
                return params.row.contactPersonalDetails[0]?.contactNumber;
            },
            renderCell: (params) => {
                return params.row.contactPersonalDetails[0]?.contactNumber;
            }
        },
        {
            field: 'designation', headerName: 'Designation', width: 150,
            filterable: true,
            valueGetter: (params) => {
                return params.row.contactPersonalDetails[0]?.designation;
            },
            renderCell: (params) => {
                return params.row.contactPersonalDetails[0]?.designation;
            }
        },
        { field: 'city', headerName: 'City', width: 150 },
        { field: 'registrationType', headerName: 'Registration Type', width: 150 },
        { field: 'gstNumber', headerName: 'Gst Number', width: 160 },
        {
            field: 'Action',
            headerName: 'Action',
            width: 130,
            renderCell: (params) => (
                <>
                    <IconButton aria-label="edit" onClick={() => handleView(params.row)}>
                        <RemoveRedEyeIcon />
                    </IconButton>
                    <IconButton aria-label="edit" onClick={() => handleEdit(params.row)}>
                        <EditIcon />
                    </IconButton>
                    <IconButton aria-label="delete" onClick={() => handleDelete(params.row._id)}>
                        <DeleteIcon />
                    </IconButton>
                </>
            )
        }
    ], []);

    return (
        <Box m="2px">
            <Header
                title="VENDORS"
                subtitle="Manage Your Vendors Here"
            />
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddVendor}>
                    Add Vendor
                </Button>
            </Box>

            <Box
                height="75vh"
                sx={useMemo(() => ({
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[700],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.greenAccent[200]} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,
                        fontSize: "13px !important",
                    },

                }), [colors])}
            >
                <VendorDataGrid data={vendors} columns={columns} />
            </Box>
            <Dialog
                fullScreen={fullScreen}
                open={openView}
                onClose={handleCloseView}
                aria-labelledby="responsive-dialog-title"
                maxWidth={"sm"}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title" style={{ backgroundColor: theme.palette.background.purple, textAlign: "center", fontWeight: 'bold', fontSize: '18px' }}>
                    {"Vendor Details"}
                </DialogTitle>
                <DialogContent style={{ backgroundColor: theme.palette.background.purple }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>Business Name:</strong> {viewData?.businessName}
                        </p>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>City:</strong> {viewData?.city}
                        </p>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>Office Address:</strong> {viewData?.officeAddress}
                        </p>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>Company Email:</strong> {viewData?.companyEmailId}
                        </p>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>Pincode:</strong> {viewData?.pincode}
                        </p>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>Registration Type:</strong> {viewData?.registrationType}
                        </p>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>City:</strong> {viewData?.city}
                        </p>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>Company Email:</strong> {viewData?.companyEmailId}
                        </p>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>Registration Type:</strong> {viewData?.registrationType}
                        </p>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>GST Number:</strong> {viewData?.gstNumber}
                        </p>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>PAN Number:</strong> {viewData?.panNumber}
                        </p>
                        <p style={{ backgroundColor: theme.palette.background.light, marginTop: 0, padding: '10px' }}>
                            <strong>Udyam Registration:</strong> {viewData?.UdyamRegistration}
                        </p>
                    </div >

                    <div style={{ flex: '1' }}>
                        <h3 style={{ textAlign: 'center' }}>Contact Persons:</h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
                            <thead>
                                <tr>
                                    <th style={{ borderBottom: `1px solid ${colors.grey[100]}`, backgroundColor: theme.palette.background.light, padding: '8px' }}>Contact Person Name</th>
                                    <th style={{ borderBottom: `1px solid ${colors.grey[100]}`, backgroundColor: theme.palette.background.light, padding: '8px' }}>Designation</th>
                                    <th style={{ borderBottom: `1px solid ${colors.grey[100]}`, backgroundColor: theme.palette.background.light, padding: '8px' }}>Department</th>
                                    <th style={{ borderBottom: `1px solid ${colors.grey[100]}`, backgroundColor: theme.palette.background.light, padding: '8px' }}>Contact Number</th>
                                    <th style={{ borderBottom: `1px solid ${colors.grey[100]}`, backgroundColor: theme.palette.background.light, padding: '8px' }}>Email Id</th>
                                </tr>
                            </thead>
                            <tbody>
                                {viewData?.contactPersonalDetails?.map((contact, index) => (
                                    <tr key={index}>
                                        <td style={{ borderBottom: `1px solid ${colors.grey[100]}`, marginBottom: '2px', padding: '10px' }}>{contact.contactPersonName}</td>
                                        <td style={{ borderBottom: `1px solid ${colors.grey[100]}`, marginBottom: '2px', padding: '10px' }}>{contact.designation}</td>
                                        <td style={{ borderBottom: `1px solid ${colors.grey[100]}`, marginBottom: '2px', padding: '10px' }}>{contact.department}</td>
                                        <td style={{ borderBottom: `1px solid ${colors.grey[100]}`, marginBottom: '2px', padding: '10px' }}>{contact.contactNumber}</td>
                                        <td style={{ borderBottom: `1px solid ${colors.grey[100]}`, marginBottom: '2px', padding: '10px' }}>{contact.emailId}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ flex: '1' }}>
                        <h3 style={{ textAlign: 'center' }}>Bank Details:</h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
                            <thead>
                                <tr>
                                    <th style={{ borderBottom: `1px solid ${colors.grey[100]}`, backgroundColor: theme.palette.background.light, padding: '8px' }}>Bank Name</th>
                                    <th style={{ borderBottom: `1px solid ${colors.grey[100]}`, backgroundColor: theme.palette.background.light, padding: '8px' }}>Branch Name</th>
                                    <th style={{ borderBottom: `1px solid ${colors.grey[100]}`, backgroundColor: theme.palette.background.light, padding: '8px' }}>Account Number</th>
                                    <th style={{ borderBottom: `1px solid ${colors.grey[100]}`, backgroundColor: theme.palette.background.light, padding: '8px' }}>Account Type</th>
                                    <th style={{ borderBottom: `1px solid ${colors.grey[100]}`, backgroundColor: theme.palette.background.light, padding: '8px' }}>IFSC Code</th>
                                </tr>
                            </thead>
                            <tbody>
                                {viewData?.bankDetails?.map((bank, index) => (
                                    <tr key={index}>
                                        <td style={{ borderBottom: `1px solid ${colors.grey[100]}`, marginBottom: '2px', padding: '10px' }}>{bank.bankName}</td>
                                        <td style={{ borderBottom: `1px solid ${colors.grey[100]}`, marginBottom: '2px', padding: '10px' }}>{bank.branchName}</td>
                                        <td style={{ borderBottom: `1px solid ${colors.grey[100]}`, marginBottom: '2px', padding: '10px' }}>{bank.accountNumber}</td>
                                        <td style={{ borderBottom: `1px solid ${colors.grey[100]}`, marginBottom: '2px', padding: '10px' }}>{bank.accountType}</td>
                                        <td style={{ borderBottom: `1px solid ${colors.grey[100]}`, marginBottom: '2px', padding: '10px' }}>{bank.IFSCCode}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </DialogContent >

                <DialogActions style={{ backgroundColor: theme.palette.background.purple }}>
                    <Button autoFocus onClick={handleCloseView} style={{ color: theme.palette.text.primary }} >
                        Close
                    </Button>
                </DialogActions>
            </Dialog >
            <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
                <DialogContent>
                    Are you sure you want to delete this vendor?
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} style={{ color: theme.palette.text.primary }}>No</Button>
                    <Button onClick={confirmDelete} style={{ color: theme.palette.text.primary }}>Yes</Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default Vendor;
